import {
  Edit3,
  Power,
} from 'lucide-react';

import ActionButton from '../../../../components/Buttons/ActionButton';
import {
  compareDecimal,
  formatMoney,
} from '../../../../utils/decimalValue';

import { InventoryItem } from '../types';

interface InventoryCardProps {
  item: InventoryItem;
  onEdit: (item: InventoryItem) => void;
  onToggleActive: (item: InventoryItem) => void;
}

const formatStock = (
  value: number | null,
  unitCode: string
) =>
  `${Number(value ?? 0).toLocaleString('es-SV', {
    maximumFractionDigits: 6,
  })} ${unitCode || ''}`.trim();

const InventoryCard = ({
  item,
  onEdit,
  onToggleActive,
}: InventoryCardProps) => {
  const currentStockClass =
    compareDecimal(item.currentStock, 0) < 0
      ? 'text-danger'
      : item.lowStock
      ? 'text-warning'
      : 'text-black dark:text-white';

  const statusClasses =
    item.lowStock && item.active
      ? 'bg-warning/15 text-warning'
      : item.active
      ? 'bg-success/10 text-success dark:bg-success/15'
      : 'bg-danger/10 text-danger dark:bg-danger/15';

  const statusLabel =
    item.lowStock && item.active
      ? 'Stock bajo'
      : item.active
      ? 'Activo'
      : 'Inactivo';

  return (
    <article
      className={`
        rounded-xl
        border
        bg-white
        p-5
        shadow-card-2
        dark:bg-boxdark
        dark:shadow-none
        ${
          item.lowStock && item.active
            ? 'border-warning/50'
            : 'border-stroke dark:border-strokedark'
        }
        ${item.active ? '' : 'opacity-65'}
      `}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold uppercase tracking-wider text-primary">
            {item.categoryName} · {item.rawMaterialCode}
          </p>

          <h3 className="mt-1 truncate font-semibold text-black dark:text-white">
            {item.rawMaterialName}
          </h3>

          <p className="mt-0.5 text-xs text-bodydark2 dark:text-bodydark">
            {item.unitName}
          </p>
        </div>

        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${statusClasses}`}
        >
          {statusLabel}
        </span>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-gray-2 p-3 dark:bg-meta-4/60">
          <p className="text-xs text-bodydark2">
            Stock actual
          </p>

          <p className={`mt-1 font-semibold ${currentStockClass}`}>
            {formatStock(
              item.currentStock,
              item.unitCode
            )}
          </p>
        </div>

        <div className="rounded-lg bg-gray-2 p-3 dark:bg-meta-4/60">
          <p className="text-xs text-bodydark2">
            Stock mínimo
          </p>

          <p className="mt-1 font-semibold text-black dark:text-white">
            {formatStock(
              item.minimumStock,
              item.unitCode
            )}
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-2 text-sm">
        <div className="flex justify-between gap-3">
          <span className="text-bodydark2">
            Stock máximo
          </span>

          <strong className="text-right text-black dark:text-white">
            {item.maximumStock == null
              ? 'Sin límite'
              : formatStock(
                  item.maximumStock,
                  item.unitCode
                )}
          </strong>
        </div>

        <div className="flex justify-between gap-3">
          <span className="text-bodydark2">
            Costo promedio
          </span>

          <strong className="text-black dark:text-white">
            {formatMoney(item.averageCost)}
          </strong>
        </div>

        <div className="flex justify-between gap-3">
          <span className="text-bodydark2">
            Última compra
          </span>

          <strong className="text-black dark:text-white">
            {formatMoney(item.lastPurchaseCost)}
          </strong>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 border-t border-stroke pt-4 dark:border-strokedark">
        <ActionButton
          label="Editar"
          icon={<Edit3 className="h-4 w-4" />}
          variant="secondary"
          size="sm"
          fullWidth
          onClick={() => onEdit(item)}
        />

        <ActionButton
          label={
            item.active ? 'Desactivar' : 'Reactivar'
          }
          icon={<Power className="h-4 w-4" />}
          variant={
            item.active ? 'danger' : 'success'
          }
          size="sm"
          fullWidth
          onClick={() => onToggleActive(item)}
        />
      </div>
    </article>
  );
};

export default InventoryCard;